import { useTranslation } from 'react-i18next'

import { DatePicker } from '@/components/ui/DatePicker'
import { useSetTrancheDate } from '@/features/cases/api/useCase'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { receivableOf, useCaseReceivables } from '@/features/finance/api/useReceivables'
import { ReceivableStatusPill } from '@/features/finance/components/ReceivableStatusPill'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { ReceivableTranche } from '@/types/domain'

/** The expected receivable series (FR-32, FR-33). Its own block rather than a
 *  fact in a grid, because each row carries an EDITABLE date: AC 3 puts the
 *  expected date in Operations' hands once delivery is known, and the 50-50
 *  split's second tranche is created undated on purpose waiting for exactly
 *  that.
 *
 *  Rendered only when there is a series. A case registered before its rate was
 *  agreed has none, and an empty table would be a heading over nothing.
 *
 *  ── Why it is a table and not a wrapping row ────────────────────────────────
 *
 *  It was `flex flex-wrap` with the picker pushed right by `ml-auto`, which put
 *  the AMOUNTS at a different x on every row — «Транш 1 12 000.00» and «Транш 2
 *  9 000.00» started their figures wherever the label before them happened to
 *  end — and left 900px of nothing between a label and its number. A column of
 *  money that does not line up is the one thing the design rule about numbers
 *  forbids, and it is worse here than anywhere: these figures are a SPLIT of
 *  one rate, and comparing them is the whole reason the series is on screen.
 *
 *  So it is the same `<table>` the cost lines above it are — the same header
 *  eyebrow, the same 18px outer gutter, the same hairline rules, the same
 *  served-total footer. Two tables in one card that agree column for column
 *  read as one document; a table over a bulleted list reads as two.
 *
 *  ── The share, and why this client is allowed to compute it ─────────────────
 *
 *  «50 / 50» is the trade's own words for what this series is, and it is the
 *  fourth column so that the table says the thing the desk actually says. It is
 *  a ratio of two figures the server already sent, in ONE currency — the same
 *  licence `ExpenseTable.groupFigures` works under, and guarded the same way: a
 *  series booked in two currencies has no share to state and prints nothing
 *  rather than a number nobody can defend (AD-9).
 *
 *  ── On a phone ───────────────────────────────────────────────────────────────
 *
 *  Four columns with an 11rem picker in the last are wider than a 390px screen,
 *  and the table used to scroll sideways with the picker cut off at the edge —
 *  an editable control a phone could not reach. Below `sm` every row becomes a
 *  two-column grid: the tranche and its amount on one line, the date under them
 *  across the full width with its own visible word, since the column heading
 *  is hidden there. The share goes: it is the one column a phone can do
 *  without, and the amounts beside each other still say 50 / 50.
 *
 *  CSS only, and ONE picker per tranche — the cell moves, it is not drawn a
 *  second time for the phone, so a test or a screen reader looking for a
 *  tranche's date finds exactly one control. Every `max-sm:` / `sm:` class is
 *  inert at `sm` and up, where the table is unchanged.
 *
 *  ── Where each tranche stands (S-21) ────────────────────────────────────────
 *
 *  Beside its label, from the case's receivables read: «Оплачен» or «Частично»
 *  when money has been allocated to it, and «заменён счётом N» when an issued
 *  invoice replaced it — the money is then the invoice's. An open tranche says
 *  nothing more: its date beside it is what is chased.
 */
/** Below `sm`, a row is a two-column grid — the label and the amount, with the
 *  date cell spanning both underneath. One string for the head, the body and
 *  the foot, so their two columns line up. */
const PHONE_ROW = 'max-sm:grid max-sm:grid-cols-[minmax(0,1fr)_auto]'

export function TrancheStrip({
  caseId,
  tranches,
  className,
}: {
  caseId: string
  tranches: ReceivableTranche[]
  /** The host's frame around the block. «Счета клиенту» gives it a card of its
   *  own (S-11 revision), where the rule above it would draw a second top
   *  border. */
  className?: string
}) {
  const { t } = useTranslation()
  const setTrancheDate = useSetTrancheDate(caseId)
  const receivables = useCaseReceivables(tranches.length > 0 ? caseId : '')

  if (tranches.length === 0) return null

  const currency = tranches[0]?.amount.currency
  const uniform = tranches.every((tranche) => tranche.amount.currency === currency)
  const total = tranches.reduce((sum, tranche) => sum + Number(tranche.amount.amount), 0)
  /** The tranche's share of the series, or `undefined` when there is nothing to
   *  take a share OF — a series summing to zero, or one booked in two
   *  currencies. */
  const share = (amount: string): number | undefined =>
    uniform && total > 0 ? (Number(amount) / total) * 100 : undefined

  return (
    /* The heavier rule, and it is the only one in the card's tail. Above it are
       the bands of COMPUTED positions — balances and profit — and below it the
       two of RECORD: what is expected and what has arrived. Hairlines inside
       each group, a line between them, which is the whole of the rhythm this
       tail used to lack. */
    <section className={cn('border-line border-t pt-5', className)}>
      <h3 className="asa-eyebrow px-[18px]">{t('cases.money.tranchesTitle')}</h3>

      <div className="asa-scroll mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-left max-sm:block">
          <thead className="max-sm:block">
            <tr className={cn('border-line border-b', PHONE_ROW)}>
              <th
                scope="col"
                className="asa-eyebrow h-9 px-[18px] max-sm:flex max-sm:items-center"
              >
                {t('cases.money.trancheColumns.tranche')}
              </th>
              <th scope="col" className="asa-eyebrow h-9 px-3 text-right max-sm:hidden">
                {t('cases.money.trancheColumns.share')}
              </th>
              <th
                scope="col"
                className="asa-eyebrow h-9 px-3 text-right max-sm:flex max-sm:items-center max-sm:justify-end max-sm:pr-[18px]"
              >
                {t('cases.money.trancheColumns.amount')}
              </th>
              <th scope="col" className="asa-eyebrow h-9 px-[18px] text-right max-sm:hidden">
                {t('cases.money.trancheDue')}
              </th>
            </tr>
          </thead>

          <tbody className="max-sm:block">
            {tranches.map((tranche) => {
              const percent = share(tranche.amount.amount)
              const served = receivableOf(receivables.data, 'tranche', tranche.id)
              const replacedBy = served?.kind === 'tranche' ? served.replacedBy : undefined
              return (
                <tr
                  key={tranche.id}
                  className={cn(
                    'border-line-hair border-b text-[13px] last:border-b-0',
                    PHONE_ROW,
                  )}
                >
                  <td className="text-text-heading px-[18px] py-2.5 align-middle">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="whitespace-nowrap">
                        {t('cases.money.trancheOfSeries', {
                          n: tranche.sequence,
                          total: tranches.length,
                        })}
                      </span>
                      {served && (served.status === 'paid' || served.status === 'partial' || served.status === 'overpaid') ? (
                        <ReceivableStatusPill row={served} />
                      ) : null}
                      {replacedBy ? (
                        <span className="text-text-muted text-[12px] font-light">
                          {t('finance.receivables.replacedBy', { number: replacedBy.number })}
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="asa-num text-text-muted px-3 py-2.5 text-right align-middle text-[12.5px] max-sm:hidden">
                    {percent === undefined ? '—' : `${formatNumber(String(percent), 0)}%`}
                  </td>
                  <td className="asa-num text-text-body px-3 py-2.5 text-right align-middle max-sm:pr-[18px]">
                    {formatNumber(tranche.amount.amount, 2)}
                    <span className="text-text-muted ml-1 text-[11.5px] font-light">
                      {tranche.amount.currency}
                    </span>
                  </td>
                  {/* The date, editable in place. PATCH replaces this one field
                      and the server has no method to recompute the amount, so
                      there is nothing else on the row to submit — which is why
                      this is a picker rather than a sheet.

                      Clearing it is legal and means "no agreed date yet", the
                      state the second tranche starts in; an empty value sends
                      null rather than omitting the key.

                      Named by its TRANCHE for a screen reader: the column
                      heading says «Ожидается» once, and a series read out as
                      "Expected, Expected" is a series nobody can act on. */}
                  <td className="px-[18px] py-2 align-middle max-sm:col-span-2 max-sm:flex max-sm:items-center max-sm:gap-3 max-sm:pt-0 max-sm:pb-2.5">
                    {/* The phone's own word for the hidden column heading.
                        Decorative: the picker is named by its tranche. */}
                    <span aria-hidden="true" className="asa-eyebrow sm:hidden">
                      {t('cases.money.trancheDue')}
                    </span>
                    <DatePicker
                      className="ml-auto w-[11rem] max-w-full min-w-0"
                      value={tranche.expectedOn ?? ''}
                      disabled={setTrancheDate.isPending}
                      aria-label={t('cases.money.trancheDueNamed', { n: tranche.sequence })}
                      onChange={(expectedOn) =>
                        setTrancheDate.mutate({
                          trancheId: tranche.id,
                          expectedOn: expectedOn === '' ? null : expectedOn,
                        })
                      }
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>

          {/* The series' own total, and the reason it is here rather than left
              to the reader: this table sits under «Дебиторка», whose figure it
              is supposed to add up to. A series that does not add up is a
              series somebody has to be able to SEE not adding up.

              Absent across currencies, for `ExpenseTable`'s reason: a sum over
              two of them is the one arithmetic this client must never do. */}
          {tranches.length > 1 && uniform ? (
            <tfoot className="max-sm:block">
              <tr className={cn('border-line border-t', PHONE_ROW)}>
                <td colSpan={2} className="text-text-muted px-[18px] py-2.5 text-[12.5px]">
                  {t('cases.money.totals')}
                </td>
                <td className="asa-num text-text-heading px-3 py-2.5 text-right text-[13px] font-semibold max-sm:pr-[18px]">
                  {formatNumber(String(total), 2)}
                  <span className="text-text-muted ml-1 text-[11.5px] font-normal">
                    {currency}
                  </span>
                </td>
                <td className="px-[18px] max-sm:hidden" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {/* A refused date change says so here rather than nowhere: there is
          no global mutation error handler, so an unread `.error` is a
          silent refusal. */}
      {setTrancheDate.error ? (
        <p role="alert" className="text-negative px-[18px] pt-3 pb-4 text-[12px] leading-snug">
          {refusalMessage(setTrancheDate.error, t)}
        </p>
      ) : null}
    </section>
  )
}
